/**
 * Markdown.jsx — renders model-written markdown as React elements.
 *
 * WHY NOT A MARKDOWN LIBRARY
 *
 * Every general-purpose markdown renderer passes raw HTML through by default,
 * because that is what the spec says to do. The usual answer is to add a
 * sanitiser and call it handled — which means two dependencies, a
 * dangerouslySetInnerHTML in the render path, and a standing dependency on
 * someone else's allowlist being right. This codebase says in ui.jsx that there
 * is no dangerouslySetInnerHTML in it outside build-time constants, and that is
 * worth more than the tag coverage a library would add.
 *
 * So this is a parser. It produces React elements from a fixed grammar and has
 * no path to innerHTML at all: anything it does not recognise ends up as a text
 * node, which React escapes. An injected <img onerror> renders as the literal
 * characters `<img onerror>`, which is exactly what should happen to it.
 *
 * WHAT IT SUPPORTS
 *
 *   # ## ###          headings
 *   - * +             bullets, one level of nesting
 *   1.                numbered lists
 *   > quote           blockquotes
 *   | a | b |         tables, with :---: alignment
 *   ```lang           fenced code
 *   ---               horizontal rule
 *   **bold** *italic* `code` [text](url)
 *
 * Tables are the reason this exists rather than reusing ArticleBody's much
 * smaller grammar: "put that in a table" is the single most common thing
 * anyone asks a general assistant for.
 *
 * LINKS ARE FILTERED, NOT TRUSTED. safeUrl rejects anything that is not
 * http(s) — javascript:, data: and vbscript: URLs never become an href, and a
 * rejected link degrades to its own text rather than disappearing.
 */

import { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import { safeUrl } from '../lib/safe';

/* ── Inline ────────────────────────────────────────────────────────────────── */

/**
 * Order matters. Code spans are matched FIRST so that `**not bold**` inside
 * backticks stays literal, which is what anyone writing about markdown expects.
 */
const INLINE = new RegExp([
  '(`[^`\\n]+`)',                        // `code`
  '(\\*\\*[^*\\n]+\\*\\*|__[^_\\n]+__)', // **bold**
  '(\\*[^*\\n]+\\*|_[^_\\n]+_)',         // *italic*
  '(\\[[^\\]\\n]*\\]\\([^)\\s]+\\))',    // [text](url)
  '(https?://[^\\s<>()]+)',              // bare URL
].join('|'), 'g');

function Link({ href, children }) {
  const safe = safeUrl(href);
  // A link we will not follow renders as plain text rather than vanishing —
  // the reader should still be able to see what was written and judge it.
  if (!safe) return <>{children}</>;

  return (
    <a href={safe} target="_blank" rel="noopener noreferrer nofollow ugc" className="md__link">
      {children}
      <ExternalLink size={11} aria-hidden="true" style={{ marginLeft: 2, verticalAlign: 'baseline' }} />
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}

function inline(text, keyPrefix = 'i') {
  const src = String(text ?? '');
  const out = [];
  let last = 0;
  let n = 0;

  // A fresh lastIndex each call: INLINE is a module-level /g regex, and reusing
  // it across calls without resetting silently skips the start of every string
  // after the first.
  INLINE.lastIndex = 0;

  for (let m = INLINE.exec(src); m; m = INLINE.exec(src)) {
    if (m.index > last) out.push(src.slice(last, m.index));
    const key = `${keyPrefix}-${n += 1}`;
    const [, code, bold, italic, link, bare] = m;

    if (code) {
      out.push(<code key={key} className="md__code">{code.slice(1, -1)}</code>);
    } else if (bold) {
      out.push(<strong key={key}>{bold.slice(2, -2)}</strong>);
    } else if (italic) {
      out.push(<em key={key}>{italic.slice(1, -1)}</em>);
    } else if (link) {
      const close = link.indexOf('](');
      const label = link.slice(1, close);
      const href = link.slice(close + 2, -1);
      out.push(<Link key={key} href={href}>{label || href}</Link>);
    } else if (bare) {
      out.push(<Link key={key} href={bare}>{bare}</Link>);
    }

    last = m.index + m[0].length;
  }

  if (last < src.length) out.push(src.slice(last));
  return out;
}

/* ── Block ─────────────────────────────────────────────────────────────────── */

const isTableSep = (line) => /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(line) && line.includes('-');

/** "| a | b |" → ['a','b']. Tolerates a missing leading or trailing pipe. */
function splitRow(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

function alignments(sep) {
  return splitRow(sep).map((c) => {
    const left = c.startsWith(':');
    const right = c.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    return 'left';
  });
}

function parse(md) {
  const lines = String(md ?? '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  const flushParagraph = (buf) => {
    if (buf.length) blocks.push({ type: 'p', text: buf.join(' ') });
    return [];
  };

  let para = [];

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code. Everything inside is literal, including things that look
    // like other blocks — that is the entire point of a fence.
    const fence = /^\s*```(\w*)\s*$/.exec(line);
    if (fence) {
      para = flushParagraph(para);
      const body = [];
      i += 1;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) { body.push(lines[i]); i += 1; }
      i += 1;   // the closing fence, or the end of input
      blocks.push({ type: 'code', lang: fence[1] || '', text: body.join('\n') });
      continue;
    }

    if (!line.trim()) { para = flushParagraph(para); i += 1; continue; }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      para = flushParagraph(para);
      blocks.push({ type: 'h', level: heading[1].length, text: heading[2] });
      i += 1;
      continue;
    }

    if (/^\s*([-*_])\s*\1\s*\1[\s\-*_]*$/.test(line)) {
      para = flushParagraph(para);
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }

    // Table: a row of pipes, immediately followed by a separator row.
    if (line.includes('|') && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      para = flushParagraph(para);
      const head = splitRow(line);
      const align = alignments(lines[i + 1]);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      blocks.push({ type: 'table', head, align, rows });
      continue;
    }

    const quote = /^\s*>\s?(.*)$/.exec(line);
    if (quote) {
      para = flushParagraph(para);
      const body = [quote[1]];
      i += 1;
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        body.push(lines[i].replace(/^\s*>\s?/, ''));
        i += 1;
      }
      blocks.push({ type: 'quote', text: body.join(' ') });
      continue;
    }

    // Lists. Indentation of two or more spaces makes an item a child of the
    // one above it — one level only, which covers every list anyone actually
    // reads and avoids a recursive parser for the ones they do not.
    const item = /^(\s*)([-*+]|\d{1,3}[.)])\s+(.*)$/.exec(line);
    if (item) {
      para = flushParagraph(para);
      const ordered = /\d/.test(item[2]);
      const items = [];
      while (i < lines.length) {
        const m = /^(\s*)([-*+]|\d{1,3}[.)])\s+(.*)$/.exec(lines[i]);
        if (!m) break;
        if (/\d/.test(m[2]) !== ordered && m[1].length === 0) break;
        items.push({ depth: m[1].length >= 2 ? 1 : 0, text: m[3] });
        i += 1;
      }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }

    para.push(line.trim());
    i += 1;
  }

  flushParagraph(para);
  return blocks;
}

/** Flat items with a depth flag → one level of real nesting. */
function nest(items) {
  const out = [];
  for (const it of items) {
    if (it.depth === 1 && out.length) {
      const parent = out[out.length - 1];
      (parent.children ||= []).push(it.text);
    } else {
      out.push({ text: it.text, children: null });
    }
  }
  return out;
}

export default function Markdown({ text, className = '' }) {
  const blocks = useMemo(() => parse(text), [text]);

  return (
    <div className={`md ${className}`}>
      {blocks.map((b, k) => {
        switch (b.type) {
          case 'h': {
            // Never an <h1>: this renders inside a page that already has one,
            // and a second h1 breaks the heading outline for screen readers.
            const Tag = `h${Math.min(b.level + 1, 5)}`;
            return <Tag key={k} className={`md__h md__h--${b.level}`}>{inline(b.text, `h${k}`)}</Tag>;
          }

          case 'hr':
            return <hr key={k} className="md__hr" />;

          case 'code':
            return (
              <pre key={k} className="md__pre">
                <code>{b.text}</code>
              </pre>
            );

          case 'quote':
            return <blockquote key={k} className="md__quote">{inline(b.text, `q${k}`)}</blockquote>;

          case 'list': {
            const Tag = b.ordered ? 'ol' : 'ul';
            return (
              <Tag key={k} className="md__list">
                {nest(b.items).map((it, j) => (
                  <li key={j}>
                    {inline(it.text, `l${k}-${j}`)}
                    {it.children && (
                      <Tag className="md__list md__list--sub">
                        {it.children.map((c, x) => <li key={x}>{inline(c, `s${k}-${j}-${x}`)}</li>)}
                      </Tag>
                    )}
                  </li>
                ))}
              </Tag>
            );
          }

          case 'table':
            return (
              /* Wrapped in its own scroller. A six-column table on a 375px
                 phone has to go somewhere, and the choice is a horizontal
                 scroll on the table or a horizontal scroll on the whole page —
                 the second one breaks every other thing on the screen. */
              <div key={k} className="md__tablewrap">
                <table className="md__table">
                  <thead>
                    <tr>
                      {b.head.map((c, j) => (
                        <th key={j} style={{ textAlign: b.align[j] || 'left' }}>
                          {inline(c, `th${k}-${j}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((row, j) => (
                      <tr key={j}>
                        {/* Indexed off the header, not the row, so a short row
                            leaves blank cells rather than a ragged table. */}
                        {b.head.map((_, x) => (
                          <td key={x} style={{ textAlign: b.align[x] || 'left' }}>
                            {inline(row[x] ?? '', `td${k}-${j}-${x}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );

          default:
            return <p key={k} className="md__p">{inline(b.text, `p${k}`)}</p>;
        }
      })}
    </div>
  );
}

export const MARKDOWN_CSS = `
.md { font-size: var(--text-base); line-height: 1.7; color: var(--ink); }
.md > * + * { margin-top: 1em; }
.md__p { margin: 0; overflow-wrap: anywhere; }

.md__h { margin: 1.6em 0 0; font-weight: 700; line-height: 1.35; color: var(--ink); }
.md__h:first-child { margin-top: 0; }
.md__h--1 { font-size: 1.25rem; letter-spacing: -0.015em; }
.md__h--2 { font-size: 1.125rem; }
.md__h--3, .md__h--4 { font-size: 1rem; }

.md__list { margin: 0; padding-left: 1.35em; }
.md__list li { margin: 0.3em 0; }
.md__list--sub { margin-top: 0.3em; padding-left: 1.2em; }

.md__quote {
  margin: 0; padding: 2px 0 2px 15px;
  border-left: 3px solid var(--primary);
  color: var(--ink-soft);
}

.md__hr { border: 0; border-top: 1px solid var(--line); margin: 1.6em 0; }

.md__code {
  padding: 2px 5px; border-radius: 4px;
  background: var(--bg-alt); border: 1px solid var(--line-strong);
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  font-size: 0.875em;
}
.md__pre {
  margin: 0; padding: 13px 15px; overflow-x: auto;
  background: var(--bg-alt); border: 1px solid var(--line-strong);
  border-radius: var(--radius-sm);
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  font-size: var(--text-sm); line-height: 1.55;
}
.md__pre code { background: none; border: 0; padding: 0; font-size: inherit; }

.md__link { color: var(--primary-text); overflow-wrap: anywhere; }
.md__link:hover { text-decoration: underline; }

/* ── Tables ───────────────────────────────────────────────────────────────── */

.md__tablewrap {
  overflow-x: auto;
  border: 1px solid var(--line-strong); border-radius: var(--radius-sm);
  /* Momentum scrolling on iOS, and a visible edge so it is obvious there is
     more table to the right rather than the content just being cut off. */
  -webkit-overflow-scrolling: touch;
}
.md__table {
  width: 100%; border-collapse: collapse;
  font-size: var(--text-sm); line-height: 1.5;
}
.md__table th, .md__table td {
  padding: 9px 12px; border-bottom: 1px solid var(--line);
  vertical-align: top; overflow-wrap: anywhere;
}
.md__table th {
  background: var(--bg-alt); font-weight: 700; color: var(--ink);
  white-space: nowrap;
  position: sticky; top: 0;
}
.md__table tr:last-child td { border-bottom: 0; }
.md__table td { color: var(--ink-soft); }
`;
